import { useEffect, useRef, useState } from 'react'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'

export function useMessageSubmit() {
  const [inputValue, setInputValue] = useState('')
  const [messageResponseIds, setMessageResponseIds] = useState<
    Record<string, string>
  >({})
  const pendingResponseIdRef = useRef<string | null>(null)

  const { messages, sendMessage, status, error } = useChat({
    transport: new DefaultChatTransport({ api: '/api/chat' }),
  })

  useEffect(() => {
    if (status !== 'ready' || !pendingResponseIdRef.current) return

    const lastAssistantMessage = messages
      .filter((m) => m.role === 'assistant')
      .at(-1)
    if (!lastAssistantMessage) return

    const responseId = pendingResponseIdRef.current
    pendingResponseIdRef.current = null

    setMessageResponseIds((prev) => ({
      ...prev,
      [lastAssistantMessage.id]: responseId,
    }))
  }, [status, messages])

  useEffect(() => {
    if (error) {
      console.error('[partner] useChat error:', error)
    }
  }, [error])

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('conversation_id')
    }
  }, [])

  const isRetrieving = status === 'submitted' || status === 'streaming'

  const onInputChange = (value: string) => {
    if (isRetrieving) return
    setInputValue(value)
  }

  const onMessageSubmit = (value?: string) => {
    const input = value || inputValue
    if (isRetrieving || !input.length) return

    const storedConversationId = sessionStorage.getItem('conversation_id') || ''
    const conversationId = storedConversationId || crypto.randomUUID()
    if (!storedConversationId) {
      sessionStorage.setItem('conversation_id', conversationId)
    }

    const responseId = crypto.randomUUID()
    pendingResponseIdRef.current = responseId

    sendMessage({ text: input }, { body: { conversationId, responseId } })
    setInputValue('')
  }

  return {
    inputValue,
    setInputValue: onInputChange,
    messageResponseIds,
    onMessageSubmit,
    isRetrieving,
    messages,
    hasError: status === 'error',
  }
}
